import React, { useEffect, useState } from 'react';
import { ChevronRight, ChevronDown, Info, FileSpreadsheet, FileDown} from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { fetchCountyAnalysis } from '../services/analysisService';
import { calculatePerformance, getHeatmapClass } from '../utils/analysisUtils';
import AchievementBar from '../components/AchievementBar';
import { exportToExcel } from '../utils/excelExportUtils';
import { exportToPDF } from '../utils/pdfExportUtils';

const CountyAnalysis = () => {
  //const [data, setData] = useState([]);
  const [expandedStreams, setExpandedStreams] = useState({});
  const [loading, setLoading] = useState(true);
  const [isTotalExpanded, setIsTotalExpanded] = useState(false);
   const [reportData, setReportData] = useState({}); // Local state to hold data for exports

  const now = new Date();
  const currentFYStart = now.getMonth() + 1 >= 7 ? now.getFullYear() : now.getFullYear() - 1;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await fetchCountyAnalysis(currentFYStart);
        
        const grouped = result.reduce((acc, row) => {
          if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
          acc[row.rev_stream].push(row);
          return acc;
        }, {});
        setReportData(grouped); // Update local state with report data for exports
        // Group by stream for the main rows
      } catch (err) {
        console.error("Analysis Load Error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentFYStart]);

  const toggleExpand = (stream) => {
    setExpandedStreams(prev => ({ ...prev, [stream]: !prev[stream] }));
  };
  // 1. Calculate Grand Totals
const grandTotals = Object.values(reportData).flat().reduce((acc, curr) => {
  acc.fy0 += curr.amount_fy0 || 0;
  acc.fyMinus1 += curr.amount_fy_minus_1 || 0;
  acc.fyMinus2 += curr.amount_fy_minus_2 || 0;
  acc.target += curr.monthly_target || 0; // Sum up targets for all streams
  return acc;
}, { fy0: 0, fyMinus1: 0, fyMinus2: 0, target: 0 });

// Total Target is the sum of unique stream targets
const totalTarget = Object.values(reportData).reduce((sum, months) => 
  sum + (months[0]?.annual_target || 0), 0);

const totalPerf = calculatePerformance(grandTotals.fy0, totalTarget);

// 2. Prepare Monthly Summary for the Entire County
const monthlyGrandTotals = Array.from({ length: 12 }, (_, i) => {
  const monthsInOrder = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];
  const monthName = monthsInOrder[i];
  
  return Object.values(reportData).flat()
    .filter(item => item.calendar_month === monthName)
    .reduce((acc, curr) => {
      acc.month = monthName;
      acc.fy0 += curr.amount_fy0 || 0;
      acc.fyMinus1 += curr.amount_fy_minus_1 || 0;
      acc.fyMinus2 += curr.amount_fy_minus_2 || 0;
      return acc;
    }, { month: monthName, fy0: 0, fyMinus1: 0, fyMinus2: 0 });
});
const handleExcelExport = () => {
    const excelData = [];
    Object.entries(reportData).map(([stream, months]) => {
      const actual = months.reduce((s, m) => s + (m.amount_fy0 || 0), 0);
      const target = months[0]?.annual_target || 0;
      const performance = target > 0 ? (actual / target) : 0;

		//Annual Data
        excelData.push({
          'Revenue Stream': stream,
          [`FY ${currentFYStart- 2}/${currentFYStart- 1}`]: months.reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0),
          [`FY ${currentFYStart- 1}/${currentFYStart}`]: months.reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0),
          [`Current FY ${currentFYStart}/${currentFYStart+ 1}`]: actual,
          'Annual-Monthly Target': target,
          'Performance %': (performance * 100).toFixed(1) + '%',
          'Status': 'Annual'
        });
		//Monthly Data
		months.forEach(m => {
        const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
		const monthlyActual = m.amount_fy0;
		const monthlyTarget = m.monthly_target || 0;
		const monthlyPerfom = monthlyTarget > 0? (monthlyActual / monthlyTarget) : 0;
        excelData.push({
          'Revenue Stream': ` => ${m.calendar_month}`,
		      [`FY ${currentFYStart- 2}/${currentFYStart- 1}`]: months.reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0),
          [`FY ${currentFYStart- 1}/${currentFYStart}`]: months.reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0),
          [`Current FY ${currentFYStart}/${currentFYStart+ 1}`]: monthlyActual,
          'Annual-Monthly Target': monthlyTarget,
		      'Performance %': (monthlyPerfom * 100).toFixed(1) + '%',
          'Status': 'Monthly'
        });
      });
      }
    );

    // Calculate Totals for the Grand Total Row
    const totalFy0 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy0 || 0), 0);
    const gtotalTarget = Object.values(reportData).reduce((s, m) => s + (m[0]?.annual_target || 0), 0);
    const gtotalPerf = gtotalTarget > 0 ? (totalFy0 / gtotalTarget) : 0;
    const grandTotalRow = {
     'Revenue Stream': 'GRAND TOTAL',
        [`FY ${currentFYStart- 2}/${currentFYStart- 1}`]: Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0),
        [`FY ${currentFYStart- 1}/${currentFYStart}`]: Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0),
        [`Current FY ${currentFYStart}/${currentFYStart+ 1}`]: totalFy0,
        'Annual-Monthly Target': gtotalTarget,
        'Performance %': (gtotalPerf * 100).toFixed(1) + '%',
        'Status': 'Grand Annual'
      };
    excelData.push(grandTotalRow);

    exportToExcel(
      excelData, 
      `County_Revenue_Analysis_${currentFYStart}.xlsx`, 
      'County Revenue Analysis'
    );
  };
  const handleDownloadPDF = () => {

    const title = `County Revenue Analysis`;
    
    const headers = ['Revenue Stream', `FY ${currentFYStart-2}/${currentFYStart-1}`, `FY ${currentFYStart-1}/${currentFYStart}`, `FY ${currentFYStart}/${currentFYStart+1}`, 'Annual-Monthly Current Target', 'Performance %'];

    const tableBody = [];
    Object.entries(reportData).forEach(([stream, months]) => {
      const actual = months.reduce((s, m) => s + (m.amount_fy0 || 0), 0);
      const target = months.find(m => m.annual_target > 0)?.annual_target || 0;
      const perf = target > 0 ? ((actual / target) * 100).toFixed(1) + '%' : '0.0%';


        const fy1 = months.reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0);
        const fy2 = months.reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0);
        tableBody.push([stream, fy2.toLocaleString(), fy1.toLocaleString(), actual.toLocaleString(), target.toLocaleString(), perf]);

      // Monthly expansion for PDF
      months.forEach(m => {
        const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
		const mActual = m.amount_fy0 || 0;
        const mTarget = m.monthly_target || 0;
		const mPerf = mTarget > 0 ? ((mActual / mTarget) * 100).toFixed(1) + '%' : '0.0%';
        const row = [`  > ${m.calendar_month}`, m.amount_fy_minus_2.toLocaleString(), m.amount_fy_minus_1.toLocaleString(), mActual.toLocaleString(), mTarget.toLocaleString(), mPerf];
        tableBody.push(row);
      });
    });
	
	// Add Grand Total to PDF
    const totalFy0 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy0 || 0), 0);
    const totalTarget = Object.values(reportData).reduce((s, m) => s + (m[0]?.annual_target || 0), 0);
    const totalPerf = totalTarget > 0 ? ((totalFy0 / totalTarget) * 100).toFixed(1) + '%' : '0.0%';

    const totalFyMinus1 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0);
    const totalFyMinus2 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0);
    tableBody.push(['GRAND TOTAL', totalFyMinus2.toLocaleString(), totalFyMinus1.toLocaleString(), totalFy0.toLocaleString(), totalPerf]);

    exportToPDF(title, headers, tableBody, `County_Revenue_Analysis.pdf`);
  };

  if (loading) return <div className="p-10 text-center">Loading Multi-Year Analysis...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border shadow-sm flex items-center justify-between">
        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <h2 className="text-xl font-bold text-gray-800">County Revenue Performance Matrix</h2>
          <p className="text-sm text-blue-800 italic">Comparing: Current FY Vs Last 2 FYs Vs Current FY Targets</p>
        </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleDownloadPDF}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-bold shadow-sm"
            >
                <FileDown size={16} /> Export PDF
              </button>
              {/*
              <button 
                onClick={handleExcelExport} disabled
                className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-sm font-bold shadow-sm"
              >
                <FileSpreadsheet size={18} className="mr-2" /> Excel
              </button>
              */}
            </div>
      </div>

      <div className="relative overflow-x-auto shadow-xl rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b">
            <tr>
              <th className="px-6 py-4 font-bold sticky left-0 bg-gray-100 z-20">Revenue Stream</th>
              <th className="px-4 py-4 text-purple-600 italic">FY {currentFYStart-2}/{currentFYStart-1}</th>
              <th className="px-4 py-4 text-purple-600 italic">FY {currentFYStart-1}/{currentFYStart}</th>
              <th className="px-4 py-4 text-blue-800 font-bold">Current FY {currentFYStart}/{currentFYStart+1}</th>
              <th className="px-4 py-4 text-gray-600">Current FY Target</th>
              <th className="px-4 py-4 text-center">Performance</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(reportData).map(([stream, months]) => {
              const actual = months.reduce((s, m) => s + m.amount_fy0, 0);
              const target = months.reduce((s, m) => s + m.monthly_target, 0);
              const perf = calculatePerformance(actual, target);
              const isExpanded = expandedStreams[stream];

              return (
                <React.Fragment key={stream}>
                  <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(stream)}>
                    <td className="px-6 py-4 font-bold flex items-center sticky left-0 bg-white">
                      {isExpanded ? <ChevronDown size={18} className="mr-2 text-blue-500" /> : <ChevronRight size={18} className="mr-2" />}
                      {stream}
                    </td>
                    <td className="px-4 py-4 text-purple-500 font-bold">
                      {months.reduce((s, m) => s + m.amount_fy_minus_2, 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-purple-500 font-bold">
                      {months.reduce((s, m) => s + m.amount_fy_minus_1, 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 font-bold text-blue-700">
                      {actual.toLocaleString()}
                      <AchievementBar actual={actual} target={target} />
                    </td>
                    <td className="px-4 py-4 font-medium">{target > 0 ? target.toLocaleString() : 'Not Set'}</td>
                    <td className="px-4 py-4 text-center">
                      <span 
                        className={`px-3 py-1 rounded-full font-bold text-xs flex items-center justify-center gap-1 ${getHeatmapClass(perf)}`}
                        data-tooltip-id="analysis-tt"
                        data-tooltip-content={`Remaining to Target: KES ${Math.max(0, target - actual).toLocaleString()}`}
                      >
                        {perf.toFixed(1)}% <Info size={12} />
                      </span>
                    </td>
                  </tr>
                  
                  {isExpanded && months.map((m, idx) => (
                    <tr key={idx} className="bg-gray-50/50 border-b text-xs italic">
                      <td className="px-12 py-2 sticky left-0 bg-gray-50">{m.calendar_month}</td>
                      <td className="px-4 py-2 text-purple-500 font-bold">{m.amount_fy_minus_2.toLocaleString()}</td>
                      <td className="px-4 py-2 text-purple-500 font-bold">{m.amount_fy_minus_1.toLocaleString()}</td>
                      <td className="px-4 py-2 text-blue-600 font-bold">{m.amount_fy0.toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-500">{m.monthly_target ? m.monthly_target.toLocaleString() : 'Not Set'}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getHeatmapClass(m.monthly_target > 0 ? (m.amount_fy0 / m.monthly_target) * 100 : 0)}`}>
                          {m.monthly_target > 0 ? ((m.amount_fy0 / m.monthly_target) * 100).toFixed(1) + '%' : '-'}
                        </span>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t-4 border-blue-200">
            {/* Sticky Grand Total Row */}
            <tr 
              className="bg-blue-100 font-extrabold cursor-pointer hover:bg-blue-200 transition-colors"
              onClick={() => setIsTotalExpanded(!isTotalExpanded)}
            >
              <td className="px-6 py-5 sticky left-0 bg-blue-100 flex items-center shadow-sm">
                {isTotalExpanded ? <ChevronDown size={20} className="mr-2 text-blue-700" /> : <ChevronRight size={20} className="mr-2 text-blue-700" />}
                GRAND TOTAL (COUNTY-WIDE)
              </td>
              <td className="px-4 py-5 text-purple-700">{grandTotals.fyMinus2.toLocaleString()}</td>
              <td className="px-4 py-5 text-purple-700">{grandTotals.fyMinus1.toLocaleString()}</td>
              <td className="px-4 py-5 text-blue-900">
                {grandTotals.fy0.toLocaleString()}
                <AchievementBar actual={grandTotals.fy0} target={totalTarget} />
              </td>
              <td className="px-4 py-5">{totalTarget.toLocaleString()}</td>
              <td className="px-4 py-5 text-center">
                <span className={`px-4 py-1.5 rounded-full text-sm font-black shadow-sm ${getHeatmapClass(totalPerf)}`}>
                  {totalPerf.toFixed(1)}%
                </span>
              </td>
            </tr>

            {/* Monthly Summary Expansion */}
            {isTotalExpanded && monthlyGrandTotals.map((m, idx) => (
              <tr key={`total-month-${idx}`} className="bg-blue-50/40 border-b text-xs font-bold italic text-blue-900/70">
                <td className="px-12 py-3 sticky left-0 bg-blue-50/40 border-r shadow-sm">
                  {m.month} (All Streams)
                </td>
                <td className="px-4 py-3 text-purple-700 font-bold">{m.fyMinus2.toLocaleString()}</td>
                <td className="px-4 py-3 text-purple-700 font-bold">{m.fyMinus1.toLocaleString()}</td>
                <td className="px-4 py-3 text-blue-900 font-bold">{m.fy0.toLocaleString()}</td>
                <td colSpan={2} className="bg-blue-50/20"></td>
              </tr>
            ))}
          </tfoot>
        </table>
        <Tooltip id="analysis-tt" />
      </div>
    </div>
  );
};

export default CountyAnalysis;